import { useState } from "react";
import CaptchaWidget from "@/components/base/CaptchaWidget";
import { submitForm, FORM_ENDPOINTS } from "@/services/forms";
import { firstProblem, validateFormData } from "@/utils/validation";

const MAX_MESSAGE = 500;

interface EnquiryFormProps {
  businessId: string;
  businessSlug: string;
  businessName: string;
}

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none";
const labelClass = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500";

/**
 * Business enquiry form on a listing (§14). Submissions are posted to our own
 * PHP handler (/forms/business-enquiry.php) which validates, rate-limits and
 * emails them, carrying the listing context as hidden fields so the lead can be
 * attributed.
 */
export default function EnquiryForm({ businessId, businessSlug, businessName }: EnquiryFormProps) {
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const problem = firstProblem(validateFormData(new FormData(form), { emailRequired: true, phoneRequired: true }));
    if (problem) {
      setStatus("error");
      setFeedback(problem);
      return;
    }
    setStatus("submitting");
    setFeedback("");
    const result = await submitForm(
      FORM_ENDPOINTS.businessEnquiry,
      new FormData(form),
      `Thank you! Your enquiry has been sent to ${businessName}. They'll get back to you soon.`,
      { captchaAction: "business_enquiry" },
    );
    if (result.ok) {
      setStatus("success");
      setFeedback(result.message);
      form.reset();
      setMessage("");
    } else {
      setStatus("error");
      setFeedback(result.message);
    }
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <h2 className="font-heading text-base font-bold text-foreground-950">Send an enquiry</h2>
      <p className="mt-1.5 text-sm text-foreground-600">
        Send your question straight to {businessName}. They'll respond directly.
      </p>

      <form onSubmit={handleSubmit} className="mt-4 space-y-4">
        <input type="hidden" name="business_name" value={businessName} />
        <input type="hidden" name="business_id" value={businessId} />
        <input type="hidden" name="business_slug" value={businessSlug} />
        <input type="hidden" name="source" value="Business profile" />

        <div>
          <label htmlFor="enq-name" className={labelClass}>Your name *</label>
          <input id="enq-name" name="name" required maxLength={120} placeholder="Full name" className={inputClass} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="enq-email" className={labelClass}>Email *</label>
            <input id="enq-email" name="email" type="email" required placeholder="you@example.com" className={inputClass} />
          </div>
          <div>
            <label htmlFor="enq-phone" className={labelClass}>Phone *</label>
            <input id="enq-phone" name="phone" required placeholder="+91 …" className={inputClass} />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label htmlFor="enq-message" className={labelClass}>Message *</label>
            <span className="text-[11px] text-foreground-400">{message.length}/{MAX_MESSAGE}</span>
          </div>
          <textarea
            id="enq-message"
            name="message"
            required
            rows={4}
            maxLength={MAX_MESSAGE}
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
            placeholder={`What would you like to know from ${businessName}?`}
            className={`${inputClass} resize-none`}
          ></textarea>
        </div>

        <input
          type="text"
          name="website_alt"
          className="form-aux-field"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />

        <CaptchaWidget />

        {status === "success" && (
          <div className="flex items-start gap-3 rounded-md border border-accent-200 bg-accent-50 p-4 text-sm text-accent-900">
            <i className="ri-checkbox-circle-line mt-0.5 text-lg text-accent-600"></i>
            <span>{feedback}</span>
          </div>
        )}
        {status === "error" && (
          <div className="flex items-start gap-3 rounded-md border border-secondary-200 bg-secondary-50 p-4 text-sm text-secondary-900">
            <i className="ri-error-warning-line mt-0.5 text-lg text-secondary-600"></i>
            <span>{feedback}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-bold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap cursor-pointer"
        >
          {status === "submitting" ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i> Sending…
            </>
          ) : (
            <>
              <i className="ri-send-plane-line"></i> Send enquiry
            </>
          )}
        </button>
      </form>
    </div>
  );
}